import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { UserDetailSection } from "../components/UserDetailDrawer";
import type { UserListItem } from "../types/users-api.types";
import type { NormalizedApiError } from "../utils/apiError";

export type UsersOverlay =
  | { kind: "none" }
  | { kind: "create" }
  | { kind: "detail"; user: UserListItem; section: UserDetailSection | null }
  | { kind: "access"; user: UserListItem }
  | { kind: "suspend"; user: UserListItem }
  | { kind: "delete"; user: UserListItem };

/**
 * Which overlay is open, and which row was just mutated.
 *
 * One overlay at a time by construction — a union rather than four booleans, so "create and detail both open"
 * is not a representable state. The ping is what tells an operator _where_ their action landed in a 25-row
 * table; it clears itself and is cancelled on unmount so a pending timer cannot set state on a dead
 * component.
 */
export function useUsersPageState() {
  const [overlay, setOverlay] = useState<UsersOverlay>({ kind: "none" });
  const [pingedId, setPingedId] = useState<string | null>(null);
  /** The last failed mutation, so a drawer can bind a 409 to the email input instead of firing a toast. */
  const [mutationError, setMutationError] = useState<NormalizedApiError | null>(null);
  const pingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Opening or closing any overlay clears the previous failure: an error from the last attempt must never
  // paint itself onto a form the operator has just opened for someone else.
  const show = useCallback((next: UsersOverlay) => {
    setMutationError(null);
    setOverlay(next);
  }, []);

  const closeOverlay = useCallback(() => show({ kind: "none" }), [show]);

  const openCreate = useCallback(() => show({ kind: "create" }), [show]);

  const openDetail = useCallback(
    (user: UserListItem, section: UserDetailSection | null = null) => show({ kind: "detail", user, section }),
    [show],
  );

  const openAccess = useCallback((user: UserListItem) => show({ kind: "access", user }), [show]);

  const openSuspend = useCallback((user: UserListItem) => show({ kind: "suspend", user }), [show]);

  const openDelete = useCallback((user: UserListItem) => show({ kind: "delete", user }), [show]);

  const ping = useCallback((id: string) => {
    if (pingTimer.current) clearTimeout(pingTimer.current);
    setPingedId(id);
    pingTimer.current = setTimeout(() => setPingedId(null), 1200);
  }, []);

  useEffect(
    () => () => {
      if (pingTimer.current) clearTimeout(pingTimer.current);
    },
    [],
  );

  // Memoised because the page feeds this object into `useUserRowViews`' dependency array. A fresh object
  // every render would defeat that memo entirely and rebuild twenty-five view models — and twenty-five
  // `actions` objects, re-rendering every row's action menu — on every keystroke in the search box.
  return useMemo(
    () => ({
      overlay,
      pingedId,
      mutationError,
      setMutationError,
      closeOverlay,
      openCreate,
      openDetail,
      openAccess,
      openSuspend,
      openDelete,
      ping,
    }),
    [
      overlay,
      pingedId,
      mutationError,
      closeOverlay,
      openCreate,
      openDetail,
      openAccess,
      openSuspend,
      openDelete,
      ping,
    ],
  );
}
